import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useNavigate, useSearchParams } from "react-router-dom";

type Mode = "signin" | "signup";

export default function SignIn() {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [sp] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const m = sp.get("m");
    if (m === "confirmed") setMsg("Email confirmed! Please sign in.");
    if (m === "timeout") setMsg("You were signed out due to inactivity. Please sign in again.");
  }, [sp]);

  async function doSignIn(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) throw error;
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      setError(err?.message ?? "Failed to sign in");
    } finally {
      setLoading(false);
    }
  }

  async function doSignUp(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: { full_name: name.trim() },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
      setMsg("Check your email for a confirmation link to finish setting up your account.");
      setMode("signin");
    } catch (err: any) {
      setError(err?.message ?? "Failed to sign up");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f3f7fb",
        display: "grid",
        placeItems: "center",
        padding: 16,
      }}
    >
      {/* Card */}
      <div
        style={{
          width: "100%",
          maxWidth: 840,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
        }}
      >
        {/* Brand side */}
        <div
          style={{
            background: "#0B2E5B",
            color: "white",
            padding: 28,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            justifyContent: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img
              src="/gc-logo.png"
              width={44}
              height={44}
              alt="GroundCampaign"
              style={{ borderRadius: 8 }}
            />
            <div style={{ fontWeight: 800, fontSize: 22, letterSpacing: 0.2 }}>
              GroundCampaign
            </div>
          </div>
          <div style={{ opacity: 0.9, marginTop: 6, lineHeight: 1.35 }}>
            Campaign Tools That Give You a Fighting Chance — From the Ground Up
          </div>
        </div>

        {/* Form side */}
        <div style={{ padding: 28 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
            <button
              onClick={() => setMode("signin")}
              disabled={mode === "signin"}
              style={tabStyle(mode === "signin")}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode("signup")}
              disabled={mode === "signup"}
              style={tabStyle(mode === "signup")}
            >
              Create Account
            </button>
          </div>

          {msg && (
            <div
              style={{
                background: "#ecfdf5",
                border: "1px solid #a7f3d0",
                color: "#065f46",
                padding: "10px 12px",
                borderRadius: 10,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {msg}
            </div>
          )}

          {error && (
            <div
              style={{
                background: "#FEF2F2",
                border: "1px solid #FCA5A5",
                color: "#991B1B",
                padding: "10px 12px",
                borderRadius: 10,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={mode === "signin" ? doSignIn : doSignUp}>
            {mode === "signup" && (
              <Field label="Full Name">
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  style={inputStyle}
                />
              </Field>
            )}

            <Field label="Email">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={inputStyle}
              />
            </Field>

            <Field label="Password">
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={inputStyle}
              />
            </Field>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                marginTop: 10,
                padding: "12px 14px",
                borderRadius: 10,
                border: "none",
                color: "white",
                background: loading ? "#9CA3AF" : "#0B2E5B",
                cursor: loading ? "default" : "pointer",
                fontWeight: 700,
              }}
            >
              {loading ? "Please wait…" : mode === "signin" ? "Sign In" : "Create Account"}
            </button>

            {mode === "signin" && (
              <div style={{ marginTop: 12, fontSize: 14 }}>
                <a href="/reset-password">Forgot your password?</a>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Mobile tweak */}
      <style>{`
        @media (max-width: 760px) {
          div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "grid", gap: 6, marginBottom: 12 }}>
      <span style={{ fontSize: 14, color: "#374151" }}>{label}</span>
      {children}
    </label>
  );
}

function tabStyle(active: boolean): React.CSSProperties {
  return {
    padding: "8px 12px",
    borderRadius: 8,
    border: `1px solid ${active ? "#0B2E5B" : "#e5e7eb"}`,
    background: active ? "rgba(11,46,91,0.06)" : "#fff",
    color: active ? "#0B2E5B" : "#374151",
    cursor: active ? "default" : "pointer",
    fontWeight: active ? 700 : 500,
  };
}

const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  outline: "none",
};
